import {
  openDossierInNewWindow,
  type DossierExportInput,
} from '../../services/export/DossierExporter';
import { useAtmosphereSettings } from '../../hooks/useAtmosphereSettings';

/**
 * A8 (2026-06-04) — "Export Mission Dossier" button.
 *
 * Sits in ScenarioRunner's History drawer. Click → opens the dossier
 * HTML in a new browser tab so the user can review + print-to-PDF via
 * the native browser dialog.
 *
 * Disabled when:
 *   - no rolls have been recorded yet (empty dossier)
 *
 * Respects A9 settings:
 *   - dossierRedactionEnabled = false suppresses the TAROT name
 *     redaction (`recognizedNpcs` is treated as containing every NPC).
 *
 * Tests inject `openFn` to assert the click path without opening a
 * real window in jsdom.
 */
export interface ExportDossierButtonProps {
  /** Dossier input EXCEPT the recognizedNpcs set — the button computes
   *  that from settings + the explicitly recognized list. */
  input: Omit<DossierExportInput, 'recognizedNpcs'>;
  /** IDs of NPCs whose Fame Recognition has been passed. */
  recognizedNpcIds: ReadonlySet<string>;
  /** Optional window.open injection (default = real window). */
  openFn?: (input: DossierExportInput) => Window | null;
  /** Optional testid override; default 'br-scenario-export-dossier-button'. */
  testid?: string;
}

export function ExportDossierButton({
  input,
  recognizedNpcIds,
  openFn = openDossierInNewWindow,
  testid = 'br-scenario-export-dossier-button',
}: ExportDossierButtonProps) {
  const { dossierRedactionEnabled } = useAtmosphereSettings();
  const disabled = input.rolls.length === 0;

  const handleClick = () => {
    // When redaction is OFF, fold every NPC into the "recognized" set
    // so the exporter renders every name + description in plain text.
    const recognized = dossierRedactionEnabled
      ? recognizedNpcIds
      : new Set(input.npcs.map((n) => n.id));
    openFn({ ...input, recognizedNpcs: recognized });
  };

  return (
    <button
      type="button"
      className="br-scenario-export-dossier-button"
      data-testid={testid}
      onClick={handleClick}
      disabled={disabled}
      title={
        disabled
          ? 'No rolls recorded — record at least one roll before exporting'
          : 'Export this mission as a styled M.I.6 dossier (opens in a new tab)'
      }
    >
      Export Dossier
    </button>
  );
}

export default ExportDossierButton;
